export const pianoSyllabus = {
  1: {
    id: 1,
    title: "Level 1 — Initial Basic (Foundation Level)",
    objective:
      "To establish instrument familiarity, rhythmic awareness, pitch recognition, hand coordination and basic musical understanding on the piano / keyboard.",
    practicalSections: [
      {
        title: "Instrument fundamentals",
        items: [
          "Parts of the piano / keyboard (keys, pedals, octave layout)",
          "Correct posture (bench height, sitting position, hand shape)",
          "Hand orientation and finger numbering (1–5 in each hand)",
        ],
      },
      {
        title: "Notes and playing",
        items: [
          "Identification of white and black keys and groups of two and three black keys",
          "Middle C concept and five-finger position exercises (hands separately)",
          "Simple melodic phrases using adjacent notes",
          "Scales (one octave, hands separately): C, G, D, A minor (natural), E minor (natural), F",
          "Major triads in root position: C, G, D, F",
          "Minor triads in root position: Am, Em, Dm",
          "Simple blocked chords and smooth chord transitions",
        ],
      },
      {
        title: "Rhythm and coordination",
        items: [
          "Whole, half and quarter notes",
          "Simple rhythmic patterns in 4/4 and 3/4 time",
          "Basic coordination of melody in the right hand with single notes or chords in the left hand",
        ],
      },
      {
        title: "Repertoire",
        items: [
          "Two pieces: one learner’s choice (any language / genre / style) and one technical or study piece (teacher / examiner guided)",
          "Optional self-composed melody (8–16 bars) for a single hand or hands separately",
        ],
      },
    ],
    theorySections: [
      {
        title: "Music fundamentals",
        items: [
          "What is music; sound vs silence",
          "Pitch and duration; high sounds vs low sounds",
        ],
      },
      {
        title: "Notes and rhythm",
        items: [
          "Musical alphabet (A–G)",
          "Naming notes on the keyboard",
          "Whole, half and quarter notes and rests",
        ],
      },
      {
        title: "Intervals and scales",
        items: [
          "Whole step / half step (distance between keys on the keyboard)",
          "Concept of major and minor scales; ascending and descending scales",
          "Structure of the major scale (W–W–H–W–W–W–H)",
        ],
      },
      {
        title: "Chords and notation",
        items: [
          "What is a chord; building a chord using three notes",
          "Basic major vs minor chord concept (happy vs sad sound)",
          "Staff basics or keyboard diagrams / note charts (optional)",
          "Time signatures: 2/4, 3/4, 4/4",
        ],
      },
    ],
  },
  2: {
    id: 2,
    title: "Level 2 — Intermediate (Developing Control)",
    objective:
      "To develop hand coordination, rhythmic precision, scale fluency, harmonic awareness and confident performance skills.",
    practicalSections: [
      {
        title: "Technique",
        items: [
          "Hands-together playing in simple textures",
          "Finger independence exercises",
          "Smooth hand movement and relaxed wrist control",
          "Basic legato and staccato articulation",
          "Balanced tone between hands",
        ],
      },
      {
        title: "Notes and playing",
        items: [
          "Expansion beyond five-finger position",
          "Melody in one hand with simple accompaniment in the other",
          "Broken chord patterns and simple broken left-hand accompaniments",
          "Controlled dynamic contrast (soft vs loud)",
          "Scales (one to two octaves, hands separately; hands together encouraged where possible): A major, E major, D minor (natural), B minor (natural), B♭ major, C minor (natural)",
          "Major and minor triads in root position and introduction to first inversion",
          "Dominant 7th chords in root position",
        ],
      },
      {
        title: "Rhythm and coordination",
        items: [
          "Eighth notes and rests",
          "Simple syncopated rhythms",
          "Steady tempo with metronome in 2/4, 3/4 and 4/4",
        ],
      },
      {
        title: "Repertoire and improvisation",
        items: [
          "Three pieces in contrasting styles or moods, at least one with hands together",
          "One accompaniment-based piece",
          "Simple improvisation over a given chord or scale; original composition permitted (optional)",
        ],
      },
    ],
    theorySections: [
      {
        title: "Rhythm",
        items: [
          "Eighth notes and rests",
          "Simple syncopation",
          "Strong and weak beats in simple time signatures",
        ],
      },
      {
        title: "Scales and intervals",
        items: [
          "Major and natural minor scales and their relative relationships",
          "Scale patterns ascending and descending",
          "Major, minor and perfect intervals and interval recognition on the keyboard",
        ],
      },
      {
        title: "Chords and harmony",
        items: [
          "Triad construction (major and minor)",
          "Dominant 7th chord structure",
          "Basic chord functions (I–IV–V) and simple chord progressions",
        ],
      },
      {
        title: "Notation",
        items: [
          "Staff notation or lead sheets / chord symbols (as appropriate)",
          "Reading simple rhythmic patterns",
        ],
      },
    ],
  },
  3: {
    id: 3,
    title: "Level 3 — Advanced 1 (Musical Expansion)",
    objective:
      "To develop technical fluency, expressive control, harmonic understanding and confident hands-together performance.",
    practicalSections: [
      {
        title: "Technique",
        items: [
          "Scales with correct fingering at developing tempos",
          "Hands-together coordination across wider ranges",
          "Broken chords and arpeggiated patterns",
          "Introduction to basic sustain pedal use",
          "Control of dynamics from soft to loud (pp–mf–ff)",
        ],
      },
      {
        title: "Notes and playing",
        items: [
          "Melodic phrasing with shape and direction",
          "Balance between melody and accompaniment",
          "Smooth hand shifts and position changes",
          "Articulation control (legato, staccato, accented notes)",
          "Scales (two octaves where possible; hands separately and hands together encouraged): D major, G minor (natural), F major (two octaves), E minor (harmonic), A♭ major, F♯ minor (natural)",
          "Seventh chords: major 7, minor 7 and dominant 7; broken chord accompaniments including Alberti bass patterns",
        ],
      },
      {
        title: "Rhythm and coordination",
        items: [
          "Triplets",
          "6/8 time and other compound meters",
          "Rhythmic contrast between hands and tempo consistency with metronome",
        ],
      },
      {
        title: "Repertoire",
        items: [
          "Three contrasting pieces (including at least one requiring pedal use)",
          "One self-arranged or self-composed piece permitted",
          "Stylistic awareness and expressive intent assessed",
        ],
      },
    ],
    theorySections: [
      {
        title: "Scales and functions",
        items: [
          "Harmonic minor scales and their sound compared to major",
          "Scale degrees and their functional roles",
        ],
      },
      {
        title: "Chords and harmony",
        items: [
          "Construction of seventh chords",
          "Diatonic harmony in major and minor keys",
        ],
      },
      {
        title: "Rhythm and form",
        items: [
          "Compound meters",
          "Awareness of simple polyrhythms",
          "Binary, ternary and verse–chorus forms and phrase structure",
        ],
      },
    ],
  },
  4: {
    id: 4,
    title: "Level 4 — Advanced 2 (Artistic Control)",
    objective:
      "To achieve confident technical control, stylistic maturity, advanced pedalling and deeper harmonic awareness.",
    practicalSections: [
      {
        title: "Technique",
        items: [
          "Scales hands together at increased tempos",
          "Arpeggios in multiple keys",
          "Advanced pedal coordination (legato and syncopated pedalling)",
          "Even tone across registers",
        ],
      },
      {
        title: "Notes and playing",
        items: [
          "Expressive shaping of long phrases and balance between voices",
          "Controlled position changes and stylistic articulation in different repertoires",
          "Scales (two octaves, hands together expected): E♭ major, C♯ minor, B major, D harmonic minor, G major in contrary motion, B♭ minor",
          "Extended chords (9ths, 11ths); broken, rolled and spread chord voicings with color awareness",
        ],
      },
      {
        title: "Rhythm and coordination",
        items: [
          "Compound and syncopated rhythms",
          "Rhythmic independence between hands",
        ],
      },
      {
        title: "Repertoire",
        items: [
          "Four pieces spanning at least two different genres or styles",
          "At least one extended or technically demanding work",
          "Interpretation and stylistic authenticity emphasised",
        ],
      },
    ],
    theorySections: [
      {
        title: "Harmony and modes",
        items: [
          "Secondary dominants and their use",
          "Cadence types (perfect, imperfect, plagal)",
          "Introduction to modes and modal sound awareness",
        ],
      },
      {
        title: "Analysis",
        items: [
          "Harmonic analysis of performed or set repertoire",
        ],
      },
    ],
  },
  5: {
    id: 5,
    title: "Level 5 — Professional",
    objective:
      "To demonstrate professional-level piano technique, stylistic versatility, improvisation skills and advanced theoretical understanding.",
    practicalSections: [
      {
        title: "Technique and scales",
        items: [
          "Scales across the full keyboard (major and natural minor)",
          "Harmonic and melodic minor scales; pentatonic and blues scales",
          "Arpeggios in all keys",
          "Speed, accuracy and endurance benchmarks and advanced pedalling and tone control",
        ],
      },
      {
        title: "Chords and rhythm",
        items: [
          "Advanced chord voicings and chord substitutions",
          "Smooth voice leading between chords",
          "Odd meters (5/4, 7/8) and polyrhythms",
        ],
      },
      {
        title: "Repertoire and improvisation",
        items: [
          "Five pieces including one technically demanding work and one original composition",
          "Multiple contrasting styles represented in the programme",
          "Improvisation over given chord progressions and style-based improvisation",
        ],
      },
    ],
    theorySections: [
      {
        title: "Advanced harmony",
        items: [
          "Modulation and voice leading",
          "Chord substitution techniques and reharmonisation",
        ],
      },
      {
        title: "Composition and notation",
        items: [
          "Writing for piano and lead-sheet creation",
          "Harmonic planning for compositions or arrangements",
        ],
      },
    ],
  },
  6: {
    id: 6,
    title: "Level 6 — Mastery (Artist / Educator / Performer Level)",
    objective:
      "To certify artistic authority, originality, pedagogical understanding and complete musical independence.",
    practicalSections: [
      {
        title: "Performance and recital",
        items: [
          "A full recital of approximately 30–45 minutes",
          "Multi-genre performance with original compositions",
          "Ensemble playing or accompaniment skills and improvisation across styles",
        ],
      },
    ],
    theorySections: [
      {
        title: "Advanced studies",
        items: [
          "Advanced harmonic and formal analysis",
          "Piano pedagogy and teaching methodology",
          "Curriculum design for piano education",
          "Music aesthetics and philosophy",
          "Optional research project, portfolio or lecture-recital submission",
        ],
      },
    ],
  },
};
